import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Eye, Plus, X, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";

/**
 * QENVA · Watchers Stack — avatar pile + add/remove popover.
 *
 * Surfaces the followers of an alert. Up to 4 avatars shown side-by-side
 * + a "+N" overflow chip. Members of the workspace can opt-in/out.
 */
function Avatar({ name, email, size = 24, title }) {
  const initials = (name || email || "?").trim()[0].toUpperCase();
  return (
    <span
      className="inline-flex items-center justify-center rounded-full bg-emerald-500/15 text-emerald-700 font-bold ring-2 ring-white"
      style={{ width: size, height: size, fontSize: size * 0.42 }}
      title={title}
    >
      {initials}
    </span>
  );
}

export default function WatchersStack({
  watchers = [],         // array of {user_id, name, email, avatar_url}
  currentUserId,
  onAdd, onRemove,
  testId = "watchers-stack",
}) {
  const { t } = useTranslation("alerts");
  const [open, setOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!open || members.length > 0) return;
    setLoading(true);
    api.get("/alm/members")
      .then((r) => setMembers(r.data?.items || []))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load members"))
      .finally(() => setLoading(false));
  }, [open, members.length]);

  const watcherIds = new Set(watchers.map((w) => w.user_id));
  const visible = watchers.slice(0, 4);
  const overflow = Math.max(0, watchers.length - 4);
  const isWatching = currentUserId ? watcherIds.has(currentUserId) : false;

  const handleAdd = async (userId) => {
    setPending(true);
    try { await onAdd(userId); } finally { setPending(false); }
  };
  const handleRemove = async (userId) => {
    setPending(true);
    try { await onRemove(userId); } finally { setPending(false); }
  };

  return (
    <div className="relative inline-flex items-center gap-2" data-testid={testId}>
      {watchers.length > 0 ? (
        <div className="flex items-center -space-x-1.5" data-testid={`${testId}-avatars`}>
          {visible.map((w) => (
            <Avatar key={w.user_id} name={w.name} email={w.email} size={24} title={w.name || w.email} />
          ))}
          {overflow > 0 && (
            <span className="inline-flex items-center justify-center rounded-full bg-zinc-200 text-zinc-700 ring-2 ring-white w-[24px] h-[24px] text-[10px] font-bold">
              +{overflow}
            </span>
          )}
        </div>
      ) : (
        <span className="text-[11.5px] text-zinc-400 italic">{t("watchers.empty")}</span>
      )}
      <button
        type="button"
        onClick={async () => {
          if (!currentUserId) { setOpen((o) => !o); return; }
          if (isWatching) {
            await handleRemove(currentUserId);
          } else {
            await handleAdd(currentUserId);
          }
        }}
        disabled={pending}
        data-testid={`${testId}-toggle`}
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11.5px] font-semibold transition-colors ${
          isWatching
            ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
            : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
        } disabled:opacity-60`}
        title={isWatching ? t("watchers.unfollow") : t("watchers.follow")}
      >
        <Eye size={11} />
        {isWatching ? t("watchers.following") : t("watchers.follow")}
      </button>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testId}-manage`}
        title={t("watchers.title")}
        className="inline-flex items-center justify-center w-6 h-6 rounded-full text-zinc-500 hover:bg-zinc-100"
      >
        <ChevronDown size={12} />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 z-30 w-[280px] bg-white rounded-xl border border-zinc-200 shadow-lg overflow-hidden"
          data-testid={`${testId}-panel`}
        >
          <header className="px-3 py-2 border-b border-zinc-100">
            <p className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-zinc-500">
              {t("watchers.title")}
            </p>
          </header>
          <ul className="max-h-64 overflow-y-auto" data-testid={`${testId}-list`}>
            {loading ? (
              <li className="px-3 py-3 text-[12px] text-zinc-400">{t("common.loading")}</li>
            ) : members.length === 0 ? (
              <li className="px-3 py-3 text-[12px] text-zinc-400">{t("assign.no_members")}</li>
            ) : members.map((m) => {
              const watching = watcherIds.has(m.user_id);
              return (
                <li key={m.user_id} className="flex items-center gap-2 px-3 py-2 hover:bg-zinc-50">
                  <Avatar name={m.name} email={m.email} size={22} />
                  <span className="flex-1 min-w-0">
                    <span className="block text-[12.5px] text-qenva-navy font-semibold truncate">
                      {m.name || m.email}
                    </span>
                    <span className="block text-[10.5px] text-zinc-500 truncate">{m.email}</span>
                  </span>
                  {watching ? (
                    <button
                      type="button"
                      disabled={pending}
                      onClick={() => handleRemove(m.user_id)}
                      data-testid={`${testId}-remove-${m.user_id}`}
                      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10.5px] font-bold text-rose-600 hover:bg-rose-50"
                    >
                      <X size={10} /> {t("watchers.remove")}
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={pending}
                      onClick={() => handleAdd(m.user_id)}
                      data-testid={`${testId}-add-${m.user_id}`}
                      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10.5px] font-bold text-emerald-700 hover:bg-emerald-50"
                    >
                      <Plus size={10} /> {t("watchers.add")}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
